import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Inbox, Send, Phone, MapPin, Euro, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { inviata: "Inviata", accettata: "Accettata", rifiutata: "Rifiutata" };
const PRICE_TYPES = [["da_definire", "Da definire"], ["fisso", "Prezzo fisso"], ["orario", "Prezzo orario"]];

function ProposalDialog({ request, open, onOpenChange, onSent }) {
  const [form, setForm] = useState({ message: "", price: "", price_type: "da_definire", estimated_time: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.message.trim()) { toast.error("Scrivi un messaggio per il cliente"); return; }
    try {
      await api.post(`/quotes/${request.id}/proposals`, { ...form, price: Number(form.price) || 0 });
      toast.success("Proposta inviata!");
      onOpenChange(false); setForm({ message: "", price: "", price_type: "da_definire", estimated_time: "" });
      onSent && onSent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141416] border-white/10 text-white">
        <DialogHeader><DialogTitle>Invia una proposta</DialogTitle></DialogHeader>
        {request && <div className="text-sm text-zinc-400 mb-1">Per: <b className="text-zinc-200">{request.title}</b></div>}
        <div className="space-y-3">
          <Textarea data-testid="prop-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Presentati e spiega come puoi aiutare…" className="bg-[#0A0A0C] border-white/10" />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><label className="text-sm text-zinc-300">Tipo prezzo</label>
              <select data-testid="prop-price-type" value={form.price_type} onChange={(e) => set("price_type", e.target.value)} className="w-full bg-[#0A0A0C] border border-white/10 rounded-md px-3 py-2 text-sm">{PRICE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            {form.price_type !== "da_definire" && <div className="space-y-1"><label className="text-sm text-zinc-300">Prezzo €</label><Input data-testid="prop-price" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="bg-[#0A0A0C] border-white/10" /></div>}
          </div>
          <div className="space-y-1"><label className="text-sm text-zinc-300">Tempistiche (opzionale)</label><Input data-testid="prop-time" value={form.estimated_time} onChange={(e) => set("estimated_time", e.target.value)} placeholder="Es. disponibile da lunedì, 2 ore/settimana" className="bg-[#0A0A0C] border-white/10" /></div>
          <Button data-testid="prop-send" onClick={submit} className="w-full bg-[#00E676] text-black font-semibold"><Send className="h-4 w-4 mr-1" /> Invia proposta</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function QuotesBoard() {
  const [tab, setTab] = useState("board");
  const [data, setData] = useState({ requests: [], approved: true });
  const [proposals, setProposals] = useState([]);
  const [dialogReq, setDialogReq] = useState(null);
  const loadBoard = async () => { try { const { data } = await api.get("/professional/quotes"); setData(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const loadProposals = async () => { try { const { data } = await api.get("/professional/proposals"); setProposals(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  useEffect(() => { loadBoard(); loadProposals(); const t = setInterval(() => { loadBoard(); loadProposals(); }, 15000); return () => clearInterval(t); }, []);

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex gap-2">
        <button data-testid="tab-board" onClick={() => setTab("board")} className={`text-sm rounded-full px-4 py-1.5 border ${tab === "board" ? "bg-[#00E676]/15 border-[#00E676] text-[#00E676]" : "border-white/10 text-zinc-300"}`}>Richieste ({data.requests.length})</button>
        <button data-testid="tab-proposals" onClick={() => setTab("proposals")} className={`text-sm rounded-full px-4 py-1.5 border ${tab === "proposals" ? "bg-[#00E676]/15 border-[#00E676] text-[#00E676]" : "border-white/10 text-zinc-300"}`}>Le mie proposte ({proposals.length})</button>
      </div>

      {data.approved === false && <div className="rounded-lg border border-amber-500/30 bg-amber-500/15 text-amber-300 px-4 py-3 text-sm">Il tuo profilo deve essere approvato per ricevere richieste di preventivo.</div>}

      {tab === "board" && (
        <div className="space-y-3">
          {data.requests.length === 0 && <div data-testid="board-empty" className="text-zinc-500 py-10 text-center"><Inbox className="h-10 w-10 mx-auto mb-2 opacity-40" />Nessuna richiesta disponibile al momento.</div>}
          {data.requests.map((r) => (
            <div key={r.id} data-testid={`request-${r.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{r.title}</span>
                {r.is_direct ? <span className="text-[10px] rounded-full bg-sky-500/15 text-sky-300 px-2 py-0.5">Diretta a te</span> : <span className="text-[10px] rounded-full bg-white/10 px-2 py-0.5">Pubblica</span>}
              </div>
              <div className="text-xs text-[#00E676] mt-0.5">{r.category}</div>
              <div className="flex items-center gap-3 text-xs text-zinc-400 mt-1 flex-wrap">
                {r.zone && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {r.zone}</span>}
                {r.budget && <span className="flex items-center gap-1"><Euro className="h-3 w-3" /> {r.budget}</span>}
                {r.preferred_date && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {r.preferred_date}</span>}
              </div>
              {r.description && <p className="text-sm text-zinc-300 mt-2">{r.description}</p>}
              <div className="mt-3">
                {r.already_proposed ? (
                  <div className="text-sm text-zinc-400 flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-[#00E676]" /> Proposta inviata ({STATUS_LABEL[r.my_proposal?.status] || "inviata"})</div>
                ) : (
                  <Button size="sm" data-testid={`send-proposal-${r.id}`} onClick={() => setDialogReq(r)} className="bg-[#00E676] text-black font-semibold"><Send className="h-4 w-4 mr-1" /> Invia proposta</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "proposals" && (
        <div className="space-y-3">
          {proposals.length === 0 && <div data-testid="proposals-empty" className="text-zinc-500 py-10 text-center">Non hai ancora inviato proposte.</div>}
          {proposals.map((p) => (
            <div key={p.id} data-testid={`myprop-${p.id}`} className={`bg-[#1C1C1F] border rounded-xl p-4 ${p.status === "accettata" ? "border-[#00E676]/50" : "border-white/10"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{p.request_title}</span>
                <span className={`text-[10px] rounded-full px-2 py-0.5 ${p.status === "accettata" ? "bg-[#00E676]/15 text-[#00E676]" : p.status === "rifiutata" ? "bg-zinc-600/30 text-zinc-400" : "bg-amber-500/15 text-amber-300"}`}>{STATUS_LABEL[p.status]}</span>
              </div>
              {p.message && <p className="text-sm text-zinc-300 mt-1">{p.message}</p>}
              {p.price > 0 && <div className="text-xs text-zinc-400 mt-1">Prezzo proposto: €{p.price}</div>}
              {p.status === "accettata" && (p.payment_status === "paid"
                ? <div className="mt-1 text-xs text-[#00E676] flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Pagato dal cliente</div>
                : p.price > 0 && <div className="mt-1 text-xs text-amber-300">In attesa di pagamento dal cliente</div>)}
              {p.status === "accettata" && p.customer_phone && (
                <div className="mt-2 text-sm text-[#00E676] flex items-center gap-1"><Phone className="h-4 w-4" /> {p.customer_name} · {p.customer_phone}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <ProposalDialog request={dialogReq} open={!!dialogReq} onOpenChange={(v) => !v && setDialogReq(null)} onSent={() => { loadBoard(); loadProposals(); }} />
    </div>
  );
}
